import { useRef, useEffect, useState } from 'react'
import { useTheme } from '../../context/ThemeContext'
import './index.scss'

// ─────────────────────────────────────────────────────────────
// PixelPortrait — a portrait as chunky, CRT-ish pixels.
//
// A canvas at 1/`scale` resolution is upscaled with
// image-rendering: pixelated, so every logical pixel becomes a
// crisp square "cube". Each frame blends a slowly flowing smoke
// field with the photo's luminance (face emerges to the right),
// perturbs it with an 8×8 Bayer matrix + per-cell hash, then
// quantizes to four hard ink steps — cells snapping between
// steps as the field drifts is what gives the old-TV shimmer.
// The pointer carries a soft lamp that brightens nearby cells.
//
// Ink color comes from --topo-ink (alpha-stepped), so all three
// themes recolor it. Renders nothing while the photo is absent:
// drop an image at public/portrait.png (or .jpg) to activate.
// ─────────────────────────────────────────────────────────────

const BAYER = [
  [0, 32, 8, 40, 2, 34, 10, 42],
  [48, 16, 56, 24, 50, 18, 58, 26],
  [12, 44, 4, 36, 14, 46, 6, 38],
  [60, 28, 52, 20, 62, 30, 54, 22],
  [3, 35, 11, 43, 1, 33, 9, 41],
  [51, 19, 59, 27, 49, 17, 57, 25],
  [15, 47, 7, 39, 13, 45, 5, 37],
  [63, 31, 55, 23, 61, 29, 53, 21],
]

// Deterministic per-cell noise
const hash2 = (x, y) => {
  let h = (x * 374761393 + y * 668265263) | 0
  h = ((h ^ (h >> 13)) * 1274126177) | 0
  return ((h ^ (h >> 16)) >>> 0) / 4294967295
}

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v)

const GLOW_R2 = 1100 // pointer lamp radius², in grid cells
const ALPHA_STEPS = [0.36, 0.56, 0.78, 1] // 4 quantized ink opacities
const CONDENSE_MS = 1800 // face condenses out of the smoke
const CONDENSE_DELAY = 900 // hold while the loader exits

const PixelPortrait = ({
  src = null, // defaults to public/portrait.png, then public/portrait.jpg
  scale = 5, // css px per pixel-cube — finer grid = more facial definition
  zoom = 0.9, // face fills more of the frame, head + shoulders still visible
  anchorY = 0.12, // vertical crop bias — small = more headroom above the hair
}) => {
  const canvasRef = useRef(null)
  const engineRef = useRef(null)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const { theme } = useTheme()

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas ? canvas.getContext('2d') : null
    if (!ctx) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let raf = 0
    let dead = false
    let visible = true
    let looping = false
    let cols = 0
    let rows = 0
    let stacked = false // ≤1200px layout — text stacks under a top portrait band
    let bandRows = 0 // rows the face may occupy (full height on desktop)
    let cell = scale // css px per cube — finer on stacked layouts (small screens
    // sit closer to the eye; 5px cubes read blurry there)
    let imgL = 0 // drawn photo rect, for melting its edges into smoke (stacked)
    let imgW = 0
    let imgB = 0 // bottom row of the drawn photo
    let luma = null // per-cell portrait luminance 0..1 (leveled)
    let negPlate = null // inverted print plate for dark-ink (light) themes
    let opa = null // per-cell portrait alpha 0..1
    let inkDark = false // dark ink on light paper → print the negative plate
    let mask = null // per-cell edge fade
    let frameData = null // reused ImageData
    let ink = [246, 215, 142]
    let startAt = Infinity
    const ptr = { x: -1e4, y: -1e4 }

    const img = new Image()

    // Resolve --topo-ink to concrete rgb for ImageData writes
    const buildInk = () => {
      const probe = document.createElement('span')
      probe.style.cssText =
        'display:none;color:var(--portrait-ink, var(--topo-ink, #f6d78e))'
      document.body.appendChild(probe)
      const c = getComputedStyle(probe).color
      probe.remove()
      const m = c.match(/(\d+)[^\d]+(\d+)[^\d]+(\d+)/)
      if (m) ink = [+m[1], +m[2], +m[3]]
      // Dark ink means light paper: switch to a positive print (ink where
      // the photo is dark) — the highlight mapping reads as a negative there
      inkDark =
        (ink[0] * 0.2126 + ink[1] * 0.7152 + ink[2] * 0.0722) / 255 < 0.5
    }

    // Sample the photo to grid resolution with adaptive contrast
    const buildLuma = () => {
      if (!img.complete || !img.naturalWidth || !cols) return
      const off = document.createElement('canvas')
      off.width = cols
      off.height = rows
      const oc = off.getContext('2d')
      if (!oc) return
      if (stacked) {
        // Stacked layout: the face is deliberately big — cover the width on
        // phones, overshoot the band on tablets — so the hero text rides
        // over the chest/shoulders instead of a small floating portrait
        const s = Math.min(
          (cols * 1.05) / img.naturalWidth,
          (bandRows * 1.35) / img.naturalHeight
        )
        const w = img.naturalWidth * s
        const h = img.naturalHeight * s
        imgL = (cols - w) / 2
        imgW = w
        const dy = (bandRows - h) * anchorY
        imgB = dy + h
        oc.drawImage(img, imgL, dy, w, h)
      } else {
        // Juba's placement: scale to a 42–58% right-side band (or full height)
        // and hug the right edge, so the face owns the right of a full-width field
        const bandLo = cols * 0.42
        const bandHi = cols * 0.58
        const s =
          Math.max(bandHi / img.naturalWidth, rows / img.naturalHeight) * zoom
        const w = img.naturalWidth * s
        const h = img.naturalHeight * s
        const dx =
          w >= cols
            ? cols / 2 - 0.57 * w
            : Math.min(cols - w, bandLo + (bandHi - w) / 2 + cols * 0.15)
        oc.drawImage(img, dx, (rows - h) * anchorY, w, h)
      }
      let data
      try {
        data = oc.getImageData(0, 0, cols, rows).data
      } catch {
        return
      }
      luma = new Float32Array(cols * rows)
      negPlate = new Float32Array(cols * rows)
      opa = new Float32Array(cols * rows)
      for (let i = 0; i < cols * rows; i++) {
        const a = data[i * 4 + 3] / 255
        opa[i] = a
        luma[i] =
          ((data[i * 4] * 0.2126 +
            data[i * 4 + 1] * 0.7152 +
            data[i * 4 + 2] * 0.0722) /
            255) *
          a
      }
      // Subject mask from RAW values, before leveling clamps them: the
      // photo's backdrop sits at ~0.01 while hair/shirt texture is 0.03+,
      // so a soft threshold keeps the backdrop off the negative plate
      for (let i = 0; i < luma.length; i++) {
        const t = clamp01((luma[i] - 0.016) / 0.029)
        negPlate[i] = t * t * (3 - 2 * t)
      }
      // Percentile levels: 45th → black, 97th → white, slight gamma lift
      const sorted = Float32Array.from(luma).sort()
      const lo = sorted[(sorted.length * 0.45) | 0]
      const hi = sorted[(sorted.length * 0.97) | 0]
      const range = Math.max(0.05, hi - lo)
      for (let i = 0; i < luma.length; i++)
        luma[i] = Math.pow(clamp01((luma[i] - lo) / range), 0.85)
      // Negative plate: ink where the photo is dark — hair, shadow side,
      // shirt — a positive print on light paper (contrast-lifted so lit
      // skin stays clean parchment)
      for (let i = 0; i < luma.length; i++)
        negPlate[i] *= Math.pow(1 - luma[i], 1.6)
    }

    const buildMask = () => {
      mask = new Float32Array(cols * rows)
      for (let y = 0; y < rows; y++)
        for (let x = 0; x < cols; x++) {
          const fl = clamp01(x / (cols * 0.04))
          const fr = clamp01((cols - 1 - x) / (cols * 0.02))
          const ft = clamp01(y / (rows * 0.06))
          const fb = clamp01((rows - 1 - y) / (rows * 0.16))
          mask[y * cols + x] = fl * fr * ft * fb
        }
    }

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      if (!rect.width || !rect.height) {
        cols = rows = 0
        return
      }
      stacked = window.matchMedia('(max-width: 1200px)').matches
      cell = stacked ? (rect.width <= 600 ? 3.5 : 4) : scale
      cols = Math.max(1, Math.round(rect.width / cell))
      rows = Math.max(1, Math.round(rect.height / cell))
      // On stacked layouts the canvas spans the whole (scrollable) hero, so
      // cap the face band to the first screen; desktop uses the full height
      bandRows = stacked
        ? Math.min(rows, (window.innerHeight * 0.66) / cell)
        : rows
      canvas.width = cols
      canvas.height = rows
      frameData = ctx.createImageData(cols, rows)
      buildMask()
      buildLuma()
    }

    const frame = (now) => {
      if (!cols || !frameData || !luma) return
      const t = now * 5e-4
      const P = reduced ? 1 : clamp01((now - startAt) / CONDENSE_MS)
      const data = frameData.data

      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const i = y * cols + x
          const o = i * 4
          const m = mask[i]
          if (m < 0.03) {
            data[o + 3] = 0
            continue
          }

          // Flowing smoke — domain-warped sines
          const wx = x + 6 * Math.sin(y * 0.08 + t * 0.6)
          const wy = y + 6 * Math.cos(x * 0.09 - t * 0.5)
          let pe =
            ((Math.sin(wx * 0.09 + t * 0.9) +
              Math.sin(wy * 0.11 - t * 0.7) +
              Math.sin((wx + wy) * 0.06 + t * 0.5)) /
              3) *
              0.5 +
            0.12

          // Pointer lamp
          const dx = x - ptr.x
          const dy = y - ptr.y
          const d2 = dx * dx + dy * dy
          if (d2 < GLOW_R2) pe += (1 - d2 / GLOW_R2) * 0.4

          // Portrait condenses out of the smoke — past mid-screen on desktop
          // (his ramp); on stacked layouts the chin melts into the smoke
          // just above where the text begins
          let ve = 0
          if (opa[i] > 0.01) {
            // Melt the photo's hard rect edges into the smoke — the chest
            // dissolves through the text zone, the sides never cut a line
            const gtx = stacked
              ? clamp01((imgB - y) / (imgB * 0.16)) *
                clamp01((x - imgL) / (imgW * 0.14)) *
                clamp01((imgL + imgW - x) / (imgW * 0.14))
              : clamp01((x / cols - 0.5) / 0.32)
            ve = gtx * opa[i] * P
            pe = pe * (1 - ve) + (inkDark ? negPlate[i] : luma[i]) * ve
          }

          // Print dither, then quantize to 4 hard ink steps —
          // cells snapping between steps as the field drifts is the shimmer
          const F =
            pe +
            ((BAYER[y & 7][x & 7] / 64 - 0.5) * 0.62 +
              (hash2(x, y) - 0.5) * 0.55) *
              0.5

          let L = -1
          if (F > 0.82 && ve > 0.22) L = 3
          else if (F > 0.52) L = 2
          else if (F > 0.36) L = 1
          else if (F > 0.2) L = 0

          if (L < 0) {
            data[o + 3] = 0
            continue
          }
          data[o] = ink[0]
          data[o + 1] = ink[1]
          data[o + 2] = ink[2]
          // Smoke runs dimmed — and extra quiet over the text (left column on
          // desktop, everything under the face band on stacked layouts);
          // face cells (ve → 1) always render at full strength
          const guard = stacked
            ? 0.35 + 0.65 * clamp01((bandRows - y) / (bandRows * 0.35))
            : 0.35 + 0.65 * clamp01((x / cols - 0.42) / 0.2)
          const dim = 0.42 * guard
          data[o + 3] = (ALPHA_STEPS[L] * 255 * m * (dim + (1 - dim) * ve)) | 0
        }
      }
      ctx.putImageData(frameData, 0, 0)
    }

    const loop = (now) => {
      if (dead) return
      frame(now)
      if (visible && !document.hidden) raf = requestAnimationFrame(loop)
      else looping = false
    }
    const wake = () => {
      if (looping || dead || reduced) return
      looping = true
      raf = requestAnimationFrame(loop)
    }

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect()
      ptr.x = (e.clientX - rect.left) / cell
      ptr.y = (e.clientY - rect.top) / cell
    }
    const onVis = () => wake()

    img.onload = () => {
      if (dead) return
      buildInk()
      resize()
      startAt = performance.now() + CONDENSE_DELAY
      setReady(true)
      if (reduced) frame(performance.now())
      else {
        wake()
        window.addEventListener('pointermove', onMove, { passive: true })
        document.addEventListener('visibilitychange', onVis)
      }
    }
    // Transparent PNG preferred — alpha melts the subject into the smoke
    const sources = src
      ? [src]
      : ['portrait.png', 'portrait.jpg'].map(
          (f) => `${process.env.PUBLIC_URL}/${f}`
        )
    let srcIdx = 0
    img.onerror = () => {
      if (dead) return
      srcIdx += 1
      if (srcIdx < sources.length) img.src = sources[srcIdx]
      else setFailed(true)
    }
    img.src = sources[0]

    let io = null
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting
        if (visible) wake()
      })
      io.observe(canvas)
    }
    let ro = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        resize()
        if (reduced) frame(performance.now())
      })
      ro.observe(canvas)
    }

    engineRef.current = {
      refreshInk() {
        if (dead) return
        buildInk()
        if (reduced) frame(performance.now())
      },
    }

    return () => {
      dead = true
      cancelAnimationFrame(raf)
      if (io) io.disconnect()
      if (ro) ro.disconnect()
      window.removeEventListener('pointermove', onMove)
      document.removeEventListener('visibilitychange', onVis)
      img.onload = img.onerror = null
      engineRef.current = null
    }
  }, [src, scale, zoom, anchorY])

  // Theme switch → re-resolve the ink rgb (next frame, after vars settle)
  useEffect(() => {
    const raf = requestAnimationFrame(() => engineRef.current?.refreshInk())
    return () => cancelAnimationFrame(raf)
  }, [theme])

  if (failed) return null

  return (
    <div
      className={`pixel-portrait${ready ? ' is-live' : ''}`}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} />
    </div>
  )
}

export default PixelPortrait
